import React, { useState, useCallback } from 'react';
import { useToggle } from 'react-use-v13';
import _ from 'lodash';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Button, Switch, TextInput, ToggleButtonGroup } from 'react-scale/components/design-system';
import { parseAnythingSeparatedIds } from '@scale/shared/utils';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { makeStyles } from 'design-system/styles';
import { IUserJson } from '@scale/shared/types/user';
import { SensitiveContentTagNames } from '@scale/shared/corp/sensitiveContentUtilsConstants';
import moment from 'moment';

const description = `
### Description: 

This tool allows you to easily onboard new or existing projects and their associated worker teams for sensitive content projects.
Note that to use this tool, worker team(s) must already exist for the project(s) you are onboarding.

Once a project has already been configured with this tool, **any future Worker Teams added to the project will automatically be configured!**

Specific functionality includes:

**Projects**
- Create a cloned version of the opt-in certification exam specific to the project
  - Create tag *${SensitiveContentTagNames.OptInFailureTemplate}* specific to the project's forked opt-in form
  - Grant this opt-in failure tag upon exam fail
- Links the forked Opt-In exam, Resilience exam, and Counseling Consent course to project via instruction items, and the worker team(s) via Required Team Courses
- Links the relevant sensitive content course (normal or high-risk) to the project via instruction items, and the worker team(s) via Required Team Courses
- Switches on the "containsAdultContent" flag on the project (to identify SC projects)
- Enables the relevant sensitive content reporting configs (normal or high-risk) for attempters and reviewers
- Adds *${SensitiveContentTagNames.SecureSCTag}* to Required Worker Tags in the project's project config
- Adds *${SensitiveContentTagNames.SecureNoSCTag}* and *${SensitiveContentTagNames.OptInFailureTemplate}* to Excluded Worker Tags in the project's project config
- Enables the optional wellness survey course configs if provided

**Worker Teams**
- Require that input projects already have worker teams assigned, and otherwise fail early and flag
- Implement an override destination worker team to send no-sensitive-content workers (default is "Free Agent/Misc") 
- Set up worker team funnels to automatically route workers off of the teams according to tags
  - Route workers with *${SensitiveContentTagNames.SecureNoSCTag}* and *${SensitiveContentTagNames.OptInFailureTemplate}* to the destination worker team
- Flag a list of workers on the team who already have *${SensitiveContentTagNames.SecureNoSCTag}* and *${SensitiveContentTagNames.OptInFailureTemplate}* tags
  - In the form of a downloadable CSV
  - Also automatically move these workers to the destination worker team (if the switch is toggled on)

#### Author: Nikash Sethi
`;

const OnboardProjectsAction: React.FC<{ user: IUserJson }> = () => {
  const classes = useStyles();

  const [projectsString, setProjectsString] = useState<string>('');
  const [destinationWorkerTeamId, setDestinationWorkerTeamId] = useState<string>('');
  const [removeIneligibleWorkers, setRemoveIneligibleWorkers] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [workersToRemoveLinks, setWorkersToRemoveLinks] = useState<string[]>([]);
  const [isHighRisk, toggleIsHighRisk] = useToggle(true);
  const [surveyId, setSurveyId] = useState<string>('');
  const [surveyFrequencyInDays, setSurveyFrequencyInDays] = useState<number>(0);
  const [surveyStartDate, setSurveyStartDate] = useState<Date>(new Date());

  const handleOnboardProjects = useCallback(async () => {
    setLoading(true);
    const projectIds = parseAnythingSeparatedIds(projectsString);
    try {
      const {
        message: responseMessage,
        error,
        workersToRemoveLinks: linksRes,
      } = await apiFetch(null, `corp-api/sensitive-content-utils/onboard-projects/`, {
        method: 'POST',
        body: {
          projectIds,
          ...(destinationWorkerTeamId.length && { destinationWorkerTeamId }),
          removeIneligibleWorkers,
          isHighRisk,
          surveyId,
          surveyFrequencyInDays,
          surveyStartDate,
        },
      });
      if (error) {
        setErrorMessage(error);
        setMessage('');
        setWorkersToRemoveLinks([]);
      } else if (responseMessage) {
        setMessage(responseMessage);
        setErrorMessage('');
        setWorkersToRemoveLinks(linksRes ?? []);
      }
    } catch (error) {
      setErrorMessage(JSON.stringify(error));
      setMessage('');
      setWorkersToRemoveLinks([]);
    }
    setLoading(false);
  }, [
    projectsString,
    destinationWorkerTeamId,
    removeIneligibleWorkers,
    isHighRisk,
    surveyId,
    surveyFrequencyInDays,
    surveyStartDate,
  ]);

  return (
    <div>
      <SanitizedMarkdown text={description} />
      <TextInput
        className={classes.marginTop}
        value={projectsString}
        label="Project IDs (anything separated)"
        fullWidth
        multiline
        required
        size="large"
        onChange={e => setProjectsString(e.target.value)}
        disabled={loading}
      ></TextInput>
      <TextInput
        className={classes.marginTop}
        value={destinationWorkerTeamId}
        label="Destination worker team ID to move no-sensitive-content workers (if blank, default team is Free Agent/Misc)"
        fullWidth
        size="large"
        onChange={e => setDestinationWorkerTeamId(e.target.value)}
        disabled={loading}
      ></TextInput>
      <TextInput
        className={classes.marginTop}
        value={surveyId}
        label="Survey course ID to assign"
        fullWidth
        size="large"
        onChange={e => setSurveyId(e.target.value)}
        disabled={loading}
      ></TextInput>
      <TextInput
        className={classes.marginTop}
        value={surveyFrequencyInDays}
        label="Survey frequency (in days)"
        fullWidth
        size="large"
        onChange={e => setSurveyFrequencyInDays(parseInt(e.target.value))}
        type="number"
        disabled={loading}
      ></TextInput>
      <TextInput
        className={classes.marginTop}
        value={moment(surveyStartDate).format('yyyy-MM-DD')}
        label="Survey start date"
        fullWidth
        size="large"
        onChange={e => setSurveyStartDate(new Date(e.target.value))}
        type="date"
        disabled={loading}
      ></TextInput>
      <div className={classes.switch}>
        <Switch
          checked={removeIneligibleWorkers}
          onChange={e => setRemoveIneligibleWorkers(e.target.checked)}
        />
        Move workers in assigned worker teams who have no-sensitive-content tags to either "Free
        Agent/Misc" or the override team specified above. If unchecked, these workers will empty
        queue until they are automatically removed from the team (within 24 hours).
      </div>
      <div className={classes.marginTop}>
        <ToggleButtonGroup
          value={isHighRisk}
          onChange={(value: boolean | null) => {
            if (!_.isNil(value)) toggleIsHighRisk(value);
          }}
          options={[
            { value: false, label: 'Normal Sensitive Content' },
            { value: true, label: 'High-Risk Sensitive Content' },
          ]}
        />
      </div>
      <Button
        className={classes.marginTop}
        type="primary"
        onClick={handleOnboardProjects}
        disabled={loading || !projectsString.length}
      >
        Submit
      </Button>
      {message.length ? <pre>{message}</pre> : <></>}
      {workersToRemoveLinks.length ? (
        workersToRemoveLinks.map(l => (
          <a key={l} href={l}>
            {l}
          </a>
        ))
      ) : (
        <></>
      )}
      {errorMessage.length ? <pre style={{ color: 'red' }}>{errorMessage}</pre> : <></>}
    </div>
  );
};

const useStyles = makeStyles({
  marginTop: {
    marginTop: 20,
  },
  switch: {
    marginTop: 20,
    margin: 10,
    display: 'flex',
    flexDirection: 'row',
  },
});

export default OnboardProjectsAction;
